/**
 * Partner Reward Modal
 * 
 * FUTURE REFACTOR NOTE:
 * Consider replacing this full-screen modal with an inline expandable panel (accordion-style),
 * similar to the "Contact & Support" section.
 * 
 * Rationale: when multiple partners or rewards are introduced, inline expansion may provide
 * faster, less disruptive UX while preserving reward visibility.
 * 
 * This refactor is intentionally postponed until after first partner validation.
 */

import { useEffect, useRef, useState } from 'react';
import { X, ExternalLink, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PartnerModalProps {
  partner: {
    id: string;
    name: string;
    description: string | null;
    website_url: string | null;
    coupon_code?: string | null;
  };
  milestoneDays: number;
  onClose: () => void;
}

// Parse coupon code from description if embedded (e.g., "...Kupón: CODE123")
// Returns { cleanDescription, couponCode }
const parseDescriptionAndCoupon = (description: string | null, explicitCouponCode?: string | null): { cleanDescription: string | null; couponCode: string | null } => {
  // If explicit coupon code is provided, use it
  if (explicitCouponCode) {
    return { cleanDescription: description, couponCode: explicitCouponCode };
  }
  
  if (!description) {
    return { cleanDescription: null, couponCode: null };
  }
  
  // Try to extract coupon from description (format: "...Kupón: CODE")
  const couponMatch = description.match(/Kupón:\s*(\S+)/i);
  if (couponMatch) {
    const couponCode = couponMatch[1];
    // Remove the coupon part from description
    const cleanDescription = description.replace(/\.?\s*Kupón:\s*\S+/i, '').trim();
    return { cleanDescription: cleanDescription || null, couponCode };
  }
  
  return { cleanDescription: description, couponCode: null };
};

// Track impressions per user per day per partner
const getImpressionKey = (userId: string, partnerId: string) => {
  const today = new Date().toISOString().split('T')[0];
  return `partner_impression_${userId}_${partnerId}_${today}`;
};

// Dynamic headline based on milestone days (Slovak copy - exact)
const getMilestoneHeadline = (days: number): string => {
  switch (days) {
    case 7:
      return 'Skvelý týždeň za tebou a skvelá ponuka pred tebou:';
    case 14:
      return '2 týždne bez alkoholu nie sú náhoda, ako ani táto ponuka:';
    case 30:
      return 'Gratulujeme a zaslúžene ťa odmeňujeme:';
    case 60:
      return 'S alkoholom si si doteraz nepripil? Tak s touto odmenou „na zdravie!"';
    case 100:
      return 'Sto dní za tebou a sto drinkov stále pred tebou? To chce osláviť:';
    case 182:
      return 'Alkohol je tvoja minulosť a táto ponuka prítomnosť:';
    case 300:
      return 'Drink v apke doteraz neťukáš, za to výhody v apke máš:';
    case 365:
      return 'Vyzerá to, že naša apka ti slúži na počítanie dní a nie drinkov. Celý rok bez alkoholu si zaslúžiš osláviť:';
    default:
      return 'Gratulujeme a zaslúžene ťa odmeňujeme:';
  }
};

export function PartnerModal({ partner, milestoneDays, onClose }: PartnerModalProps) {
  const { user } = useAuth();
  const impressionTracked = useRef(false);
  const [copied, setCopied] = useState(false);

  // Parse description to separate coupon code from benefit text
  const { cleanDescription, couponCode } = parseDescriptionAndCoupon(
    partner.description,
    partner.coupon_code
  );

  // Lock body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Track impression on mount (once per day per partner)
  useEffect(() => {
    if (!user || impressionTracked.current) return;
    
    const impressionKey = getImpressionKey(user.id, partner.id);
    const alreadyTracked = sessionStorage.getItem(impressionKey);
    
    if (!alreadyTracked) {
      // Track impression
      supabase
        .from('partner_events')
        .insert({
          user_id: user.id,
          partner_id: partner.id,
          milestone_days: milestoneDays,
          event_type: 'impression',
        })
        .then(({ error }) => {
          if (!error) {
            sessionStorage.setItem(impressionKey, 'true');
          }
        });
    }
    
    impressionTracked.current = true;
  }, [user, partner.id, milestoneDays]);

  const handleCtaClick = async () => {
    if (!user) return;
    
    // Track click (always)
    await supabase
      .from('partner_events')
      .insert({
        user_id: user.id,
        partner_id: partner.id,
        milestone_days: milestoneDays,
        event_type: 'click',
      });

    // Open partner website
    if (partner.website_url) {
      window.open(partner.website_url, '_blank', 'noopener,noreferrer');
    }
    
    onClose();
  };

  const handleCopyCoupon = () => {
    if (couponCode) {
      navigator.clipboard.writeText(couponCode);
      setCopied(true);
      // Use position: 'top-center' to avoid overlapping CTA button
      toast.success('Skopírované do schránky', {
        position: 'top-center',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Extract partner display name from name or URL
  const getPartnerDisplayName = () => {
    if (partner.name) return partner.name;
    if (partner.website_url) {
      try {
        const url = new URL(partner.website_url);
        return url.hostname.replace('www.', '');
      } catch {
        return partner.website_url;
      }
    }
    return 'partner';
  };

  const headline = getMilestoneHeadline(milestoneDays);

  return (
    <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm flex flex-col items-center p-6 overflow-y-auto overscroll-contain" style={{ maxHeight: '100dvh' }}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
        aria-label="Close"
      >
        <X className="h-8 w-8 text-foreground" />
      </button>
      
      <div className="w-full max-w-sm flex flex-col items-center space-y-3 my-auto py-4">
        {/* Dynamic headline based on milestone - above image, less prominent */}
        <p className="text-sm text-muted-foreground text-center leading-relaxed">
          {headline}
        </p>

        {/* Partner visual - 4:3 aspect ratio, between headline and offer */}
        <div className="w-full">
          <AspectRatio ratio={4/3} className="bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
            {/* Fallback to check icon - same as unlocked reward icon */}
            <div className="flex items-center justify-center h-full w-full">
              <Check className="h-14 w-14 text-primary" />
            </div>
          </AspectRatio>
        </div>
        
        {/* Partner benefit description - MORE prominent (primary focal point) */}
        {cleanDescription && (
          <h1 className="text-xl font-serif font-semibold text-foreground text-center leading-snug">
            {cleanDescription}
          </h1>
        )}
        
        {/* Coupon section - separate from description */}
        {couponCode && (
          <div className="w-full space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              Kód kupónu:
            </p>
            <div 
              onClick={handleCopyCoupon}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-muted/50 rounded-lg border border-border cursor-pointer hover:bg-muted transition-colors"
            >
              <span className="font-mono font-bold text-lg text-foreground tracking-wide">
                {couponCode}
              </span>
              {copied ? (
                <Check className="h-5 w-5 text-primary shrink-0" />
              ) : (
                <Copy className="h-5 w-5 text-muted-foreground shrink-0" />
              )}
            </div>
          </div>
        )}
        
        {/* CTA with partner name only + icon after text */}
        {partner.website_url && (
          <Button 
            onClick={handleCtaClick}
            className="w-full h-14 text-lg gap-3"
            size="lg"
          >
            {getPartnerDisplayName()}
            <ExternalLink className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
}
